import React, { forwardRef, useRef, useState } from "react";
import { colord } from "colord";
import { ColorPicker as ColorPickerInput } from "@showitinc/color-picker";
import { Text } from "@showitinc/showit-wc";
import styles from "./DesignSettings.module.css";
import { getDisplayColor } from "../../app2components/base/ColorPicker";
import Flyout from "../../app2components/base/Flyout";

const ThemeColorPicker = ({
  theme,
  setTheme,
  color,
  themeIndex,
  totalColors,
}) => {
  const inputRef = useRef();
  const [flyoutRef, setFlyoutRef] = useState(null);

  const effectiveTotalColors = totalColors || 8;
  const isLastThreeSwatches = themeIndex >= effectiveTotalColors - 3;

  const _openPicker = async () => {
    if (flyoutRef && inputRef.current) {
      flyoutRef.passedTarget = inputRef.current;
      flyoutRef.showPopover(inputRef.current);
      if (typeof flyoutRef.updateVisuals === "function") {
        await flyoutRef.updateVisuals();
      }
      if (flyoutRef.flyout) {
        const swatchRect = inputRef.current.getBoundingClientRect();
        const flyoutElement = flyoutRef.flyout;

        flyoutElement.style.position = "absolute";
        // Calculate dynamic offset for last three swatches to keep them in view
        const swatchHeight = swatchRect.height;
        const positionFromEnd = themeIndex - (effectiveTotalColors - 3);
        const topOffset = isLastThreeSwatches
          ? -(swatchHeight * positionFromEnd)
          : 0;
        flyoutElement.style.top = `${swatchRect.top + topOffset}px`;
        flyoutElement.style.left = `${swatchRect.right + 4}px`;
      }
    }
  };

  const _closePicker = () => flyoutRef && flyoutRef.hidePopover();
  const displayColor = getDisplayColor(theme, color);

  const onThemeChange = (e) => {
    const { r, g, b } = e.detail.color.rgba;
    setTheme(
      theme.setIn(["colors", String(themeIndex)], colord({ r, g, b }).toHex())
    );
  };

  return (
    <>
      <div
        ref={inputRef}
        onClick={_openPicker}
        style={{ backgroundColor: color }}
        className={styles.themePicker}
      >
        <div className={styles.themePickerNumberContainer}>
          <Text className={styles.themePickerNumber}>{themeIndex + 1}</Text>
        </div>
      </div>
      <Flyout
        className={styles.themePickerFlyout}
        refSetter={setFlyoutRef}
        hideHeader
      >
        <ColorPickerInput
          hideAlpha
          color={displayColor.toHex()}
          eyedropper
          onSIInput={(e) => onThemeChange(e)}
          onSIChange={onThemeChange}
          onSISubmit={(e) => {
            onThemeChange(e);
            _closePicker();
          }}
        />
      </Flyout>
    </>
  );
};

const DesignSettingsColorPalette = forwardRef(({ theme, setTheme }, ref) => {
  const colors = theme.get("colors", []);
  const totalColors = colors.length;

  return (
    <div className={styles["color-palette-section"]} ref={ref}>
      <div className={styles.themePickersContainer}>
        {colors.map((color, index) => (
          <ThemeColorPicker
            // eslint-disable-next-line
            key={`theme-${index}`}
            theme={theme}
            setTheme={setTheme}
            color={color}
            themeIndex={index}
            totalColors={totalColors}
          />
        ))}
      </div>
    </div>
  );
});

export default DesignSettingsColorPalette;
